/**
 * Step two of revoke. It does not ask "are you sure?" — it reads out an inventory.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THIS IS THE MOST CONSEQUENTIAL CONTROL IN THE DASHBOARD.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Task 20 made revocation **terminal**: a revoked device cannot be approved back and
 * cannot be revoked twice — both are `409 WORKFORCE_INVALID_STATE_TRANSITION` with an
 * **empty** `allowed_transitions[]`, which is how the first revocation's actor,
 * timestamp and reason survive. And it closes **every** `wp_device_credentials` row
 * for the device in one transaction, *including one still inside its rotation grace*,
 * for a reason worth repeating in full because it is the reason the grace is not an
 * exception: **that graced key is exactly the one an attacker who took the machine
 * before the last rotation is holding.**
 *
 * ── Why the copy is a list of five specific losses ──────────────────────────
 *
 * "Are you sure?" transfers no information. The operator already believes they are
 * sure; what they do not know is what the button costs. Five things are true after
 * this call and each is a separate sentence because each is separately surprising:
 *
 *   1. The Agent's local queue is destroyed — with the number, from
 *      `DeviceHealth.pending_local_events`, and with **`null` never rendered as 0**.
 *   2. Every credential closes, the graced one included, with the reason why.
 *   3. It is terminal. The machine comes back only as a *new* device row with a new
 *      `device_id`, on a new registration token.
 *   4. Events **already ingested are untouched** — the one thing revoke does *not*
 *      destroy. An operator who believes it does will hesitate over the wrong
 *      decision, or worse, revoke in order to delete somebody's data.
 *   5. The machine is not told. Its next call is one indistinguishable `401`, because
 *      telling a prober which of four things happened is an oracle.
 *
 * ── The gate ────────────────────────────────────────────────────────────────
 *
 * Two independent inputs (`revokeGate` in `web/src/devices/fleet.ts`): a reason of at
 * least three characters — which `DeviceRevokeRequest`, `wp_devices_revoke_ck` and
 * `wp_device_cred_revoke_ck` all demand, so it is checked here to be learned from the
 * form rather than from a `400` — and the device's own handle typed back. One
 * mis-click satisfies neither.
 *
 * The submit button is **absent**, not disabled, until the gate opens: a disabled
 * control here would read as "the system will not let you", when the truth is "you
 * have not finished saying what you are doing". The requirement is stated in words
 * beside the fields.
 */

import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';

import { useI18n } from '../../i18n';
import type { Device, DeviceHealth } from '../../api/types';
import { queueLoss, revokeGate, revokeHandle, REVOKE_REASON_MIN } from '../../devices/fleet';
import { StatusNote } from '../../states/StatusNote';

export function RevokeConfirm({
  device,
  health,
  pending,
  onCancel,
  onConfirm,
}: {
  readonly device: Device;
  /** `null` when the health read failed or has not landed — a real case, §queueLoss. */
  readonly health: DeviceHealth | null;
  readonly pending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (reason: string) => void;
}): ReactNode {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [handleDraft, setHandleDraft] = useState('');
  const reasonRef = useRef<HTMLInputElement | null>(null);

  const handle = revokeHandle(device);
  const gate = revokeGate(handle, reason, handleDraft);
  const loss = queueLoss(health);

  // Focus lands on the first thing the operator must supply, not on the panel. The
  // shape file's §11 makes this the acceptance criterion for this step.
  useEffect(() => {
    reasonRef.current?.focus();
  }, []);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (!gate.ready) return;
    onConfirm(reason.trim());
  };

  return (
    <StatusNote
      severity="critical"
      role="alert"
      title={t('devices.revoke.title', { name: handle })}
      testId="k5-revoke-confirm"
    >
      <p>{t('devices.revoke.lead')}</p>

      <ul className="wp-list" data-testid="k5-revoke-losses">
        <li data-testid="k5-revoke-queue">
          {loss.kind === 'known'
            ? t('devices.revoke.queueKnown', { events: loss.events })
            : loss.kind === 'empty'
              ? t('devices.revoke.queueEmpty')
              : t('devices.revoke.queueUnknown')}
        </li>
        <li data-testid="k5-revoke-credentials">{t('devices.revoke.credentials')}</li>
        <li data-testid="k5-revoke-terminal">{t('devices.revoke.terminal')}</li>
        <li data-testid="k5-revoke-keeps">{t('devices.revoke.keepsIngested')}</li>
        <li data-testid="k5-revoke-silent">{t('devices.revoke.silent')}</li>
      </ul>

      <form onSubmit={submit}>
        <label className="wp-field" htmlFor="k5-revoke-reason">
          <span className="wp-field__label">
            {t('devices.revoke.reason', { min: REVOKE_REASON_MIN })}
          </span>
          <input
            id="k5-revoke-reason"
            ref={reasonRef}
            className="wp-field__input"
            value={reason}
            aria-describedby="k5-revoke-reason-hint"
            onChange={(event) => {
              setReason(event.target.value);
            }}
            data-testid="k5-revoke-reason"
          />
        </label>
        <p className="wp-small wp-muted" id="k5-revoke-reason-hint">
          {t('devices.revoke.reasonHint')}
        </p>

        <label className="wp-field" htmlFor="k5-revoke-handle">
          <span className="wp-field__label">{t('devices.revoke.handle', { handle })}</span>
          <input
            id="k5-revoke-handle"
            className="wp-field__input wp-mono"
            value={handleDraft}
            aria-describedby="k5-revoke-handle-hint"
            onChange={(event) => {
              setHandleDraft(event.target.value);
            }}
            data-testid="k5-revoke-handle"
          />
        </label>
        <p className="wp-small wp-muted" id="k5-revoke-handle-hint">
          {t('devices.revoke.handleHint')}
          {device.hostname === null ? ` ${t('devices.revoke.handleFallback')}` : ''}
        </p>

        <div className="wp-c-actions">
          {/* Absent rather than disabled — see this file's header. */}
          {gate.ready ? (
            <button
              type="submit"
              className="wp-button wp-c-button--danger-solid"
              data-testid="k5-revoke-submit"
            >
              {pending ? t('devices.revoke.confirming') : t('devices.revoke.confirm')}
            </button>
          ) : (
            <p className="wp-small wp-muted" data-testid="k5-revoke-blocked">
              {t('devices.revoke.blocked')}
            </p>
          )}
          <button
            type="button"
            className="wp-button wp-button--quiet"
            onClick={onCancel}
            data-testid="k5-revoke-cancel"
          >
            {t('devices.revoke.cancel')}
          </button>
        </div>
      </form>
    </StatusNote>
  );
}
